import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  AlertTriangle, Bell, ChevronRight, ChevronDown, ChevronUp, Clock, Eye, FileText,
  Hammer, Layers, Lightbulb, Palette, Pencil, Plus, Timer,
  TrendingUp, Users, Zap, Box, Clapperboard, Sparkles, CircleDot, X, Monitor, Trash2,
  Image as ImageIcon, Music, Link as LinkIcon, Download
} from 'lucide-react';
import WorkOrderBuilder from '@/components/WorkOrderBuilder';
import InvoiceBuilderModal, { InvoiceList } from '@/components/InvoiceBuilderModal';
import VendorDashboard from '@/pages/VendorDashboard';
import FreelancerDashboardNew from '@/pages/FreelancerDashboardNew';
import EmployeeDashboard from '@/pages/EmployeeDashboard';
import { AuthProvider } from '@/contexts/AuthContext';
import { rawMaterialCounts, BUCKET_LABEL, BUCKET_SHORT } from '@/lib/rawMaterialMap';
import AuthImage from '@/components/AuthImage';
import { downloadWorkOrderPdf } from '@/lib/woPdfDownload';

/**
 * VendorFreelancerPicker — custom dropdown with reliable color coding.
 *
 * Founder rule (May 12, 2026): Orange = Freelancer, Blue = Vendor.
 * Native HTML `<option>` elements have unreliable styling on macOS Chrome
 * (they ignore inline `color` styles in many cases), so we render a fully
 * custom popover. Freelancer rows also show a Skill tag on the right
 * (Modelling / Animator / Rigging / Lighting / etc.) derived from the
 * freelancer's profile primary_skill or their recent task history.
 */
function VendorFreelancerPicker({ vendors, selectedId, onChange, pendingByUser = {} }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // Close on outside click.
  React.useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const isFreelancer = (v) => v && (v.kind === 'freelancer' || v.entity_type === 'freelancer');
  const studios = vendors.filter(v => !isFreelancer(v));
  const freelancers = vendors.filter(isFreelancer);
  const current = vendors.find(v => v.id === selectedId);

  const Row = ({ v }) => {
    const fl = isFreelancer(v);
    const isSel = v.id === selectedId;
    const name = v.company_name || v.full_name || v.email || '—';
    const skill = fl ? (v.primary_skill || (v.skills && v.skills[0]) || '') : (v.entity_type || '');
    // WO-pending badge — look up by vendor id AND by user_id (some
    // freelancers in this dropdown use user_id as their picker id).
    const pending = pendingByUser[v.id] || pendingByUser[v.user_id] || null;
    return (
      <button
        type="button"
        onClick={() => { onChange(v.id); setOpen(false); }}
        data-testid={`picker-row-${v.id}`}
        className={`w-full px-3 py-2 flex items-center justify-between gap-3 text-left transition-colors text-xs ${
          isSel ? (fl ? 'bg-amber-500/15' : 'bg-blue-500/15') : 'hover:bg-zinc-800'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${fl ? 'bg-amber-400' : 'bg-blue-400'}`} />
          <span className={`truncate ${fl ? 'text-amber-300' : 'text-blue-300'} ${isSel ? 'font-semibold' : ''}`}>
            {name}
          </span>
        </div>
        {pending && pending.count > 0 && (
          <span
            className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-violet-500/20 text-violet-300 border border-violet-500/40"
            data-testid={`wo-pending-badge-${v.id}`}
            title={`${pending.count} unbilled task${pending.count === 1 ? '' : 's'} · ~₹${Number(pending.amount).toLocaleString('en-IN')} — picking this vendor and clicking Create Work Order will auto-fill these into the WO`}
          >
            WO · {pending.count}
          </span>
        )}
        {skill && (
          <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
            fl
              ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
              : 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
          }`}>
            {skill}
          </span>
        )}
      </button>
    );
  };

  const fl = isFreelancer(current);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="vendor-command-vendor-select"
        aria-expanded={open}
        className="h-9 min-w-[260px] bg-zinc-900 border border-zinc-700 rounded-md px-3 flex items-center justify-between gap-2 text-xs hover:border-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-[#09090b]"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${fl ? 'bg-amber-400' : 'bg-blue-400'}`} />
          <span className={`truncate ${fl ? 'text-amber-300' : 'text-blue-300'}`}>
            {current ? (current.company_name || current.full_name || current.email) : 'Select…'}
          </span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 w-[340px] max-h-[480px] bg-zinc-950 border border-zinc-700 rounded-md shadow-2xl overflow-hidden flex flex-col"
          data-testid="vendor-picker-dropdown"
        >
          <div className="overflow-y-auto flex-1">
            {studios.length > 0 && (
              <>
                <div className="sticky top-0 z-10 px-3 py-1.5 bg-blue-500/10 border-b border-blue-500/20 text-[10px] font-bold uppercase tracking-widest text-blue-300">
                  Vendors / Studios · {studios.length}
                </div>
                {studios.map(v => <Row key={v.id} v={v} />)}
              </>
            )}
            {freelancers.length > 0 && (
              <>
                <div className="sticky top-0 z-10 px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/20 text-[10px] font-bold uppercase tracking-widest text-amber-300">
                  Freelancers · {freelancers.length}
                </div>
                {freelancers.map(v => <Row key={v.id} v={v} />)}
              </>
            )}
            {vendors.length === 0 && (
              <p className="p-6 text-center text-xs text-zinc-500">No vendors or freelancers found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const DEPT_ICONS = {
  'Asset Build': Box, 'Rigging': Hammer, 'Animation': Clapperboard,
  'Lighting': Lightbulb, 'Compositing': Layers, 'FX': Sparkles,
  'Concept Art': Palette, 'Layout': Layers, 'Render': Zap,
  'LRC': Eye, 'Sound': CircleDot,
};

const KANBAN_COLS = [
  { key: 'ready', label: 'READY', color: '#f59e0b', border: 'border-amber-500' },
  { key: 'in_progress', label: 'IN PROGRESS', color: '#3b82f6', border: 'border-blue-500' },
  { key: 'review', label: 'IN REVIEW', color: '#a855f7', border: 'border-purple-500' },
  { key: 'approved', label: 'APPROVED', color: '#10b981', border: 'border-emerald-500' },
  { key: 'completed', label: 'COMPLETED', color: '#6b7280', border: 'border-zinc-500' },
];

const STAGE_COLORS = {
  'Asset Build': 'bg-amber-500/20 text-amber-400', 'Rigging': 'bg-blue-500/20 text-blue-400',
  'Animation': 'bg-purple-500/20 text-purple-400', 'Lighting': 'bg-yellow-500/20 text-yellow-300',
  'Compositing': 'bg-teal-500/20 text-teal-400', 'FX': 'bg-pink-500/20 text-pink-400',
  'Concept Art': 'bg-rose-500/20 text-rose-400', 'Render': 'bg-cyan-500/20 text-cyan-400',
};

const PRIORITY_COLORS = { high: 'text-red-400', medium: 'text-amber-400', low: 'text-emerald-400' };

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function currency(v) {
  return `\u20B9${Number(v || 0).toLocaleString('en-IN')}`;
}

/* ─── ACTION BAR ─── */
function ActionBar({ alertCount, onCreateWO, onViewWO, onToggleAlerts, onLiveView, showAlerts, liveViewActive }) {
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="action-bar">
      <button onClick={onCreateWO} data-testid="create-wo-btn"
        className="h-10 px-5 bg-amber-500 hover:bg-amber-600 text-zinc-950 text-sm font-heading font-bold tracking-wide rounded-md flex items-center gap-2 transition-all hover:-translate-y-0.5">
        <Plus className="w-4 h-4" /> Create Work Order
      </button>
      <button onClick={onViewWO} data-testid="view-active-wo-btn"
        className={`h-10 px-5 border text-sm font-heading tracking-wide rounded-md flex items-center gap-2 transition-all hover:-translate-y-0.5 ${showAlerts === 'wo' ? 'bg-amber-500/10 border-amber-500/40 text-amber-400' : 'bg-zinc-900 border-zinc-700 hover:border-zinc-500 text-zinc-300'}`}>
        <Eye className="w-4 h-4" /> View Active Work Orders
      </button>
      <div className="relative" data-testid="alerts-btn">
        <button onClick={onToggleAlerts}
          className={`h-10 px-4 border text-sm font-heading tracking-wide rounded-md flex items-center gap-2 transition-all ${showAlerts === 'alerts' ? 'bg-red-500/10 border-red-500/40 text-red-400' : 'bg-zinc-900 border-zinc-700 hover:border-zinc-500 text-zinc-300'}`}>
          <Bell className="w-4 h-4" /> Alerts
        </button>
        {alertCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white font-bold flex items-center justify-center">{alertCount}</span>
        )}
      </div>
      <button onClick={onLiveView} data-testid="live-view-btn"
        className={`h-10 px-5 border text-sm font-heading font-bold tracking-wide rounded-md flex items-center gap-2 transition-all hover:-translate-y-0.5 ${
          liveViewActive ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400' : 'bg-zinc-900 border-zinc-700 hover:border-emerald-500/40 text-zinc-300 hover:text-emerald-400'
        }`}>
        <Monitor className="w-4 h-4" /> Live View
      </button>
    </div>
  );
}

/* ─── WORK ORDERS HERO ─── */
function WorkOrdersHero({ workOrders, onCreateWO, onOpenWO, onEditWO, onDeleteWO }) {
  const activeCount = workOrders.filter(w => w.status !== 'cancelled' && w.status !== 'closed').length;
  const pendingCount = workOrders.filter(w => w.status === 'draft' || w.status === 'issued').length;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="work-orders-hero">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-heading font-bold text-white uppercase tracking-wide">Work Orders</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Active: <span className="text-emerald-400 font-semibold">{activeCount}</span>
            <span className="mx-2 text-zinc-700">|</span>
            Pending Approval: <span className="text-amber-400 font-semibold">{pendingCount}</span>
          </p>
        </div>
      </div>

      {workOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <FileText className="w-10 h-10 text-zinc-700 mb-3" />
          <p className="text-sm text-zinc-500">No work orders yet</p>
          <button onClick={onCreateWO} className="mt-3 h-9 px-5 bg-amber-500 hover:bg-amber-600 text-zinc-950 text-xs font-heading font-bold rounded-md flex items-center gap-1.5 transition-all">
            <Plus className="w-3.5 h-3.5" /> Build Detailed Work Order
          </button>
        </div>
      ) : (
        <div className="flex overflow-x-auto gap-4 pb-2 snap-x scrollbar-thin scrollbar-thumb-zinc-700" data-testid="wo-cards-scroll">
          {workOrders.slice(0, 10).map(wo => {
            const statusColor = wo.status === 'accepted' ? 'border-l-emerald-500' :
              wo.status === 'issued' ? 'border-l-purple-500' :
              wo.status === 'draft' ? 'border-l-amber-500' : 'border-l-zinc-600';
            const statusBadge = wo.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' :
              wo.status === 'issued' ? 'bg-purple-500/20 text-purple-400' :
              wo.status === 'draft' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-700 text-zinc-400';
            return (
              <div key={wo.id} data-testid={`wo-card-${wo.id}`}
                className={`min-w-[320px] snap-start bg-zinc-950 border border-zinc-800 ${statusColor} border-l-4 rounded-md p-4 flex-shrink-0 hover:border-zinc-600 transition-colors`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-heading font-bold text-white">{wo.work_order_no}</span>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusBadge}`}>
                    {(wo.status || 'draft').charAt(0).toUpperCase() + (wo.status || 'draft').slice(1)}
                  </span>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Project:</span>
                    <span className="text-zinc-200 font-medium truncate ml-2 max-w-[180px]">{wo.project_name || wo.scope_title || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Start Date</span>
                    <span className="text-zinc-300 font-mono text-[11px]">{formatDate(wo.start_date || wo.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">End Date</span>
                    <span className="text-zinc-300 font-mono text-[11px]">{formatDate(wo.end_date)}</span>
                  </div>
                  <div className="border-t border-zinc-800 pt-1.5 mt-1.5">
                    <span className="text-zinc-500">Scope: </span>
                    <span className="text-zinc-300">{wo.lines?.length || 0} items</span>
                    <span className="mx-1.5 text-zinc-700">|</span>
                    <span className="text-zinc-500">Budget: </span>
                    <span className="text-amber-400 font-semibold">{currency(wo.total_amount)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => onOpenWO(wo)} data-testid={`wo-open-${wo.id}`}
                    className="flex-1 h-8 bg-amber-500 hover:bg-amber-600 text-zinc-950 text-xs font-heading font-bold rounded flex items-center justify-center gap-1 transition-all">
                    Open
                  </button>
                  <button onClick={() => onEditWO(wo)} data-testid={`wo-edit-${wo.id}`}
                    className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-heading rounded flex items-center justify-center gap-1 transition-all">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => onDeleteWO(wo.id)} data-testid={`wo-delete-${wo.id}`}
                    className="h-8 px-2 bg-zinc-800 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 text-xs rounded flex items-center justify-center transition-all"
                    title="Delete Work Order">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── ALLOTTED WORK STATUS ─── */
function AllottedWorkStatus({ departments }) {
  const deptEntries = Object.entries(departments || {}).filter(([_, v]) => v.total > 0);
  if (deptEntries.length === 0) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="allotted-work-status">
        <h2 className="text-lg font-heading font-bold text-white uppercase tracking-wide mb-4">Allotted Work Status</h2>
        <p className="text-sm text-zinc-500 text-center py-4">No department tasks allotted yet</p>
      </div>
    );
  }
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="allotted-work-status">
      <h2 className="text-lg font-heading font-bold text-white uppercase tracking-wide mb-4">Allotted Work Status</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {deptEntries.map(([name, stats]) => {
          const Icon = DEPT_ICONS[name] || Box;
          const hasReady = stats.ready > 0;
          return (
            <div key={name} data-testid={`dept-card-${name.toLowerCase().replace(/ /g, '-')}`}
              className="bg-zinc-950 border border-zinc-800 rounded-md p-4 hover:border-zinc-600 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-md bg-zinc-800 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-zinc-400" />
                </div>
                <span className="text-sm font-heading font-semibold text-white">{name}</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Ready</span>
                  <span className={`font-mono font-bold ${hasReady ? 'text-emerald-400' : 'text-zinc-600'}`}>{stats.ready}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">In Progress</span>
                  <span className="font-mono font-bold text-blue-400">{stats.in_progress}</span>
                </div>
                {stats.blocked > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Blocked</span>
                    <span className="font-mono font-bold text-red-400">{stats.blocked}</span>
                  </div>
                )}
              </div>
              <button data-testid={`assign-artist-${name.toLowerCase().replace(/ /g, '-')}`}
                className="mt-3 w-full h-8 bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 text-amber-400 text-xs font-heading font-semibold rounded flex items-center justify-center gap-1 transition-all">
                <Users className="w-3 h-3" /> Assign Artist
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── DEADLINES & RISKS ─── */
function DeadlinesRisks({ deadlines, overdueTasks }) {
  const dl = deadlines || { overdue: 0, due_today: 0, on_track: 0, next_deadline: null };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="deadlines-risks">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-heading font-bold text-white uppercase tracking-wide">Deadlines & Risks</h2>
        <button className="text-xs text-amber-500 hover:text-amber-400 font-heading tracking-wide flex items-center gap-1">
          View All Tasks <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3 min-w-[120px]">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <span className="text-lg font-heading font-bold text-red-400">{dl.overdue}</span>
          </div>
          <span className="text-xs text-red-400 font-semibold">Overdue</span>
        </div>
        <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-md px-4 py-3 min-w-[120px]">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
            <span className="text-lg font-heading font-bold text-amber-400">{dl.due_today}</span>
          </div>
          <span className="text-xs text-amber-400 font-semibold">Due Today</span>
        </div>
        <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-4 py-3 min-w-[120px]">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <span className="text-lg font-heading font-bold text-emerald-400">{dl.on_track}</span>
          </div>
          <span className="text-xs text-emerald-400 font-semibold">On Track</span>
        </div>
      </div>
      {dl.next_deadline && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-md p-3">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Next Deadline</p>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm text-amber-400 font-heading font-semibold">{dl.next_deadline.name}</span>
              <span className="text-xs text-zinc-500 mx-2">&middot;</span>
              <span className="text-xs text-zinc-400">{dl.next_deadline.stage}</span>
              <span className="text-xs text-zinc-500 mx-2">&middot;</span>
              <span className="text-xs text-zinc-300 font-mono">{formatDate(dl.next_deadline.due_date)}</span>
            </div>
            {dl.overdue > 0 && (
              <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-semibold">High Priority</span>
            )}
          </div>
        </div>
      )}
      {overdueTasks?.length > 0 && (
        <div className="mt-3 space-y-1">
          {overdueTasks.slice(0, 3).map((t, i) => (
            <div key={i} className="flex items-center justify-between text-xs bg-zinc-950 rounded px-3 py-1.5">
              <span className="text-red-400">{t.name}</span>
              <span className="text-zinc-600 font-mono text-[10px]">{formatDate(t.due_date)}</span>
            </div>
          ))}
          {overdueTasks.length > 3 && (
            <p className="text-[10px] text-zinc-600 text-right">And {overdueTasks.length - 3} more overdue</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── TASK BOARD (KANBAN) ─── */
function TaskBoard({ kanban }) {
  const kb = kanban || { ready: [], in_progress: [], review: [], approved: [], completed: [] };
  const hasAny = Object.values(kb).some(arr => arr.length > 0);
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="task-board">
      <h2 className="text-lg font-heading font-bold text-white uppercase tracking-wide mb-4">Task Board</h2>
      {!hasAny ? (
        <p className="text-sm text-zinc-500 text-center py-6">No tasks in the pipeline yet</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
          {KANBAN_COLS.map(col => (
            <div key={col.key} className="min-w-0" data-testid={`kanban-col-${col.key}`}>
              <div className={`flex items-center justify-between mb-2 pb-2 border-b-2 ${col.border}`}>
                <span className="text-[11px] font-heading font-bold tracking-wider" style={{ color: col.color }}>
                  {col.label} ({kb[col.key]?.length || 0})
                </span>
                <Plus className="w-3 h-3 text-zinc-600" />
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700 pr-1">
                {(kb[col.key] || []).slice(0, 15).map(task => {
                  const stageClass = STAGE_COLORS[task.stage] || 'bg-zinc-700/50 text-zinc-400';
                  return (
                    <div key={task.id} data-testid={`kanban-card-${task.id}`}
                      className="bg-zinc-950 border border-zinc-800 rounded p-3 hover:border-zinc-600 transition-colors cursor-pointer">
                      <div className="flex items-start justify-between mb-1.5">
                        <span className="text-xs font-semibold text-white leading-tight">{task.name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 ml-1 ${stageClass}`}>{task.stage}</span>
                      </div>
                      {task.asset && <p className="text-[10px] text-zinc-500 mb-1.5">Asset: {task.asset}</p>}
                      {task.is_blocked && <p className="text-[10px] text-red-400 mb-1">Blocked</p>}
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center">
                            <span className="text-[8px] text-zinc-300 font-bold">{(task.assigned_to || 'U')[0]}</span>
                          </div>
                          <span className="text-[10px] text-zinc-500 truncate max-w-[80px]">{task.assigned_to || 'Unassigned'}</span>
                        </div>
                        {task.due_date && (
                          <span className="text-[10px] text-zinc-600 font-mono flex items-center gap-0.5">
                            <Clock className="w-2.5 h-2.5" /> {formatDate(task.due_date)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── PERFORMANCE OVERVIEW ─── */
function PerformanceOverview({ data }) {
  const hs = data.health_score || 0;
  const hb = data.health_breakdown || { speed: 0, quality: 0, reliability: 0 };
  const cap = data.capacity || {};
  const daily = data.daily_output || [];
  const maxOutput = Math.max(...daily.map(d => d.completed), 1);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="performance-overview">
      <h2 className="text-lg font-heading font-bold text-white uppercase tracking-wide mb-4">Performance Overview</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Health Score */}
        <div className="flex items-center gap-5">
          <div className="relative w-24 h-24 flex-shrink-0">
            <svg className="w-24 h-24 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15" fill="none" stroke="#27272a" strokeWidth="2.5" />
              <circle cx="18" cy="18" r="15" fill="none"
                stroke={hs >= 7 ? '#10b981' : hs >= 5 ? '#f59e0b' : '#ef4444'}
                strokeWidth="2.5" strokeDasharray={`${hs * 10} 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-heading font-bold text-white">{hs}</span>
              <span className="text-[8px] text-zinc-500">/ 10</span>
            </div>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Quality', value: hb.quality, color: 'bg-emerald-500' },
              { label: 'Timeliness', value: hb.speed, color: 'bg-amber-500' },
              { label: 'Reliability', value: hb.reliability, color: 'bg-blue-500' },
            ].map(m => (
              <div key={m.label} className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${m.color}`} />
                <span className="text-[11px] text-zinc-400 w-16">{m.label}</span>
                <span className="text-[11px] font-mono text-zinc-300">{m.value}</span>
              </div>
            ))}
            <button className="text-[10px] text-amber-500 hover:text-amber-400 font-heading flex items-center gap-0.5 mt-1">
              View Details <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Capacity vs Usage */}
        <div>
          <p className="text-xs text-zinc-500 font-heading uppercase tracking-wider mb-3">Capacity vs Usage</p>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
                <span>Declared Capacity</span>
                <span className="font-mono">{cap.declared_total || 0}</span>
              </div>
              <div className="w-full h-3 bg-zinc-800 rounded overflow-hidden">
                <div className="h-full bg-blue-500 rounded" style={{ width: '100%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
                <span>Active Usage</span>
                <span className="font-mono">{cap.active_tasks || 0}</span>
              </div>
              <div className="w-full h-3 bg-zinc-800 rounded overflow-hidden">
                <div className="h-full bg-amber-500 rounded" style={{ width: `${Math.min(cap.utilization || 0, 100)}%` }} />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center mt-3 gap-2">
            <div className="relative w-14 h-14">
              <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="14" fill="none" stroke="#27272a" strokeWidth="3" />
                <circle cx="18" cy="18" r="14" fill="none" stroke={cap.utilization > 60 ? '#10b981' : cap.utilization > 30 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="3" strokeDasharray={`${Math.min(cap.utilization || 0, 100)} 100`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[11px] font-mono font-bold text-white">{cap.utilization || 0}%</span>
              </div>
            </div>
            <span className="text-[10px] text-zinc-500">Capacity<br/>Used</span>
          </div>
          <button className="text-[10px] text-amber-500 hover:text-amber-400 font-heading flex items-center gap-0.5 mt-2">
            View Details <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Output Trend */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-zinc-500 font-heading uppercase tracking-wider">Output Trend (Last 7 Days)</p>
            <button className="text-[10px] text-amber-500 hover:text-amber-400 font-heading flex items-center gap-0.5">
              View Details <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="h-24 flex items-end gap-1">
            {daily.map((d, i) => {
              const h = Math.max(4, (d.completed / maxOutput) * 100);
              const isToday = i === daily.length - 1;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] font-mono text-zinc-400">{d.completed}</span>
                  <div className="w-full relative group">
                    <div className={`w-full rounded-sm transition-all ${isToday ? 'bg-amber-500' : 'bg-blue-500/60'}`}
                      style={{ height: `${h}%`, minHeight: '3px' }} />
                  </div>
                  <span className="text-[7px] text-zinc-600">{d.date?.slice(5)}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-end mt-2">
            <span className="text-[10px] text-zinc-500">Avg: <span className="font-mono text-zinc-300">{data.avg_daily_output || 0}</span>/day</span>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   IMPERSONATED DASHBOARD — renders the exact dashboard
   the selected vendor/freelancer/employee sees
   ═══════════════════════════════════════════════════════════ */
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImpersonationContext = React.createContext(null);

function ImpersonatedAuthProvider({ children, targetUserId, adminToken, targetUser }) {
  const impersonatedApi = useMemo(() => {
    const instance = axios.create({ baseURL: API_BASE });
    instance.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    instance.defaults.headers.common['X-Impersonate'] = targetUserId;
    return instance;
  }, [adminToken, targetUserId]);

  const value = {
    user: targetUser,
    token: adminToken,
    login: async () => {},
    logout: () => {},
    loading: false,
    api: impersonatedApi,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

function ImpersonatedDashboard({ vendorId, vendors, adminToken, onClose }) {
  const vendor = vendors.find(v => v.id === vendorId);
  const [targetUser, setTargetUser] = useState(null);
  const [liveNavView, setLiveNavView] = useState('dashboard');
  const { api } = useAuth();

  useEffect(() => {
    if (!vendor) return;
    const uid = vendor.user_id;
    api.get(`/admin/live-view/dashboard/${uid}`).then(r => {
      setTargetUser({
        id: r.data.user?.user_id || uid,
        user_id: r.data.user?.user_id || uid,
        email: r.data.user?.email || vendor.email || '',
        full_name: r.data.user?.full_name || vendor.company_name || vendor.full_name || '',
        role: r.data.user?.role || 'vendor_producer',
        role_label: r.data.user?.role_label || 'Vendor',
        workforce_type: r.data.user?.workforce_type || '',
        _dashboard_type: r.data.dashboard_type,
      });
    }).catch(console.error);
  }, [api, vendor]);

  if (!vendor) return null;

  if (!targetUser) return (
    <div className="bg-zinc-900 border border-emerald-500/20 rounded-md p-8 text-center">
      <div className="animate-pulse text-zinc-500 text-sm font-heading">Loading {vendor.company_name || vendor.full_name}'s live dashboard...</div>
    </div>
  );

  const dashType = targetUser._dashboard_type;

  return (
    <div className="bg-zinc-900 border border-emerald-500/30 rounded-md overflow-hidden flex flex-col max-h-[80vh]" data-testid="live-view-panel">
      {/* Live View Header */}
      <div className="px-5 py-3 bg-emerald-500/10 border-b border-emerald-500/20 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Monitor className="w-5 h-5 text-emerald-400" />
          <div>
            <p className="text-sm font-heading font-bold text-emerald-300">
              Live View: {targetUser.full_name}
            </p>
            <p className="text-[10px] text-zinc-500">{targetUser.role_label} &middot; {targetUser.email} &middot; Dashboard type: {dashType}</p>
          </div>
        </div>
        <button onClick={onClose} data-testid="close-live-view"
          className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-heading rounded flex items-center gap-1.5 transition-all">
          <X className="w-3.5 h-3.5" /> Close Live View
        </button>
      </div>
      {/* Sidebar + Dashboard layout (scrollable) */}
      <div className="flex flex-1 min-h-0">
        {/* Mini Sidebar — shows what vendor sees */}
        <VendorLiveSidebar dashType={dashType} activeView={liveNavView} onNavigate={setLiveNavView} />
        {/* Dashboard content */}
        <div className="flex-1 p-5 min-w-0 overflow-y-auto" data-testid="live-view-scroll">
          <ImpersonatedAuthProvider targetUserId={targetUser.user_id} adminToken={adminToken} targetUser={targetUser}>
            <LiveDashboardRouter dashType={dashType} view={liveNavView} />
          </ImpersonatedAuthProvider>
        </div>
      </div>
    </div>
  );
}

function DashboardSwitcher({ dashType }) {
  const ctx = React.useContext(ImpersonationContext);
  if (!ctx) return null;
  if (dashType === 'vendor') return <VendorDashboardLive ctx={ctx} />;
  if (dashType === 'freelancer' || dashType === 'vendor_artist') return <FreelancerDashboardLive ctx={ctx} />;
  if (dashType === 'employee') return <EmployeeDashboardLive ctx={ctx} />;
  return <p className="text-xs text-zinc-500">Unknown dashboard type: {dashType}</p>;
}

/* ─── VENDOR LIVE SIDEBAR ─── */
const VENDOR_SIDEBAR_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: Layers },
  { key: 'messages', label: 'Message Center', icon: FileText },
  { key: 'tasks', label: 'My Tasks', icon: Layers },
  { key: 'team', label: 'My Team', icon: Users },
  { key: 'submit', label: 'Submit for Approval', icon: Eye },
  { key: 'approvals', label: 'Approvals', icon: Eye },
  { key: 'timeline', label: 'Timeline', icon: Clock },
  { key: 'finance', label: 'Finance', icon: TrendingUp },
  { key: 'resources', label: 'Resources', icon: FileText },
  { key: 'agent', label: 'Vawworld Agent', icon: Sparkles },
  { key: 'ticket', label: 'Raise Ticket', icon: AlertTriangle },
  { key: 'profile', label: 'Profile', icon: Users },
];

const FREELANCER_SIDEBAR_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: Layers },
  { key: 'messages', label: 'Message Center', icon: FileText },
  { key: 'tasks', label: 'My Tasks', icon: Layers },
  { key: 'submit', label: 'Submit for Approval', icon: Eye },
  { key: 'approvals', label: 'Approvals', icon: Eye },
  { key: 'resources', label: 'Resources', icon: FileText },
  { key: 'profile', label: 'Profile', icon: Users },
];

const EMPLOYEE_SIDEBAR_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: Layers },
  { key: 'tasks', label: 'Tasks', icon: Layers },
  { key: 'submit', label: 'Submit for Approval', icon: Eye },
  { key: 'approvals', label: 'Approvals', icon: Eye },
  { key: 'timeline', label: 'Timeline', icon: Clock },
  { key: 'profile', label: 'Profile', icon: Users },
];

function VendorLiveSidebar({ dashType, activeView, onNavigate }) {
  const items = dashType === 'vendor' ? VENDOR_SIDEBAR_ITEMS :
    dashType === 'freelancer' || dashType === 'vendor_artist' ? FREELANCER_SIDEBAR_ITEMS :
    EMPLOYEE_SIDEBAR_ITEMS;

  return (
    <div className="w-44 border-r border-zinc-800 bg-zinc-950 py-3 flex-shrink-0" data-testid="live-sidebar">
      {items.map(item => (
        <button key={item.key} onClick={() => onNavigate(item.key)}
          data-testid={`live-nav-${item.key}`}
          className={`w-full flex items-center gap-2 px-4 py-2 text-[11px] font-heading tracking-wide transition-colors ${
            activeView === item.key ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-zinc-500 hover:text-zinc-300 border-l-2 border-transparent'
          }`}>
          <item.icon className="w-3.5 h-3.5" />
          {item.label}
        </button>
      ))}
    </div>
  );
}

function LiveDashboardRouter({ dashType, view }) {
  const ctx = React.useContext(ImpersonationContext);
  if (!ctx) return null;

  if (view === 'dashboard') {
    if (dashType === 'vendor') return <VendorDashboardLive ctx={ctx} />;
    if (dashType === 'freelancer' || dashType === 'vendor_artist') return <FreelancerDashboardLive ctx={ctx} />;
    return <EmployeeDashboardLive ctx={ctx} />;
  }
  if (view === 'tasks') return <FreelancerDashboardLive ctx={ctx} />;
  if (view === 'messages') return <LiveMessagesView ctx={ctx} />;
  if (view === 'team') return <LiveTeamView ctx={ctx} />;
  if (view === 'finance') return <LiveFinanceView ctx={ctx} />;
  if (view === 'approvals') return <LiveApprovalsView ctx={ctx} />;
  if (view === 'submit') return <LiveSubmitView ctx={ctx} />;
  if (view === 'timeline') return <LiveTimelineView ctx={ctx} />;
  if (view === 'resources') return <LiveResourcesView />;
  if (view === 'agent') return <LiveAgentView />;
  if (view === 'ticket') return <LiveTicketView ctx={ctx} />;
  if (view === 'profile') return <LiveProfileView ctx={ctx} />;
  return <VendorDashboardLive ctx={ctx} />;
}

function LiveSubmitView({ ctx }) {
  const [submissions, setSubmissions] = useState([]);
  useEffect(() => {
    ctx.api.get('/reviews/inbox').then(r => {
      setSubmissions(r.data?.items || r.data || []);
    }).catch(() => setSubmissions([]));
  }, [ctx.api]);
  return (
    <div className="space-y-3" data-testid="live-submit">
      <h4 className="text-xs font-heading text-zinc-400 uppercase">Submit for Approval</h4>
      <p className="text-[10px] text-zinc-500">Submissions go through: Supervisor → Producer → Client pipeline</p>
      {submissions.length === 0 ? (
        <div className="bg-zinc-950 border border-zinc-800 rounded-md py-8 text-center">
          <p className="text-xs text-zinc-500">No submissions made yet by this vendor</p>
          <p className="text-[10px] text-zinc-600 mt-1">When the vendor submits work for approval, it will appear here</p>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {submissions.slice(0, 20).map(s => (
            <div key={s.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-3 py-2.5">
              <div>
                <p className="text-xs text-zinc-200">{s.title || s.name || 'Submission'}</p>
                <p className="text-[10px] text-zinc-600">{s.kind} &middot; {s.stage} &middot; {s.created_at?.slice(0, 10)}</p>
              </div>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                s.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' :
                s.status === 'changes_requested' ? 'bg-amber-500/15 text-amber-400' :
                'bg-blue-500/15 text-blue-400'
              }`}>{(s.status || 'pending').replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveTimelineView({ ctx }) {
  const [tasks, setTasks] = useState([]);
  useEffect(() => { ctx.api.get('/tasks').then(r => setTasks(r.data || [])).catch(() => {}); }, [ctx.api]);
  const withDates = tasks.filter(t => t.due_date).sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
  return (
    <div className="space-y-3" data-testid="live-timeline">
      <h4 className="text-xs font-heading text-zinc-400 uppercase">Timeline</h4>
      {withDates.length === 0 ? (
        <div className="bg-zinc-950 border border-zinc-800 rounded-md py-8 text-center">
          <p className="text-xs text-zinc-500">No scheduled tasks for this vendor</p>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {withDates.slice(0, 20).map(t => {
            const isOverdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed';
            return (
              <div key={t.id} className={`flex items-center justify-between bg-zinc-950 border rounded px-3 py-2 ${isOverdue ? 'border-red-500/30' : 'border-zinc-800'}`}>
                <div><p className="text-xs text-zinc-200">{t.title || t.name || t.parent_code || (t.task_type ? t.task_type.replace(/_/g, ' ').toUpperCase() : '—')}</p><p className="text-[10px] text-zinc-600">{t.task_type}</p></div>
                <div className="text-right">
                  <p className={`text-[10px] font-mono ${isOverdue ? 'text-red-400' : 'text-zinc-400'}`}>{t.due_date?.slice(0, 10)}</p>
                  <p className="text-[9px] text-zinc-600">{t.status?.replace(/_/g, ' ')}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LiveResourcesView() {
  return (
    <div className="space-y-3" data-testid="live-resources">
      <h4 className="text-xs font-heading text-zinc-400 uppercase">Resources / Standards</h4>
      <div className="bg-zinc-950 border border-zinc-800 rounded-md p-4">
        <p className="text-xs text-zinc-300">Naming conventions, production guidelines, and reference materials</p>
        <p className="text-[10px] text-zinc-600 mt-2">This section shows the vendor's access to naming rules and production standards</p>
      </div>
    </div>
  );
}

function LiveAgentView() {
  return (
    <div className="space-y-3" data-testid="live-agent">
      <h4 className="text-xs font-heading text-zinc-400 uppercase">Vawworld Agent</h4>
      <div className="bg-zinc-950 border border-zinc-800 rounded-md p-4">
        <p className="text-xs text-zinc-300">AI production assistant</p>
        <p className="text-[10px] text-zinc-600 mt-2">Chat history and AI interactions are private to each vendor</p>
      </div>
    </div>
  );
}

function LiveTicketView({ ctx }) {
  const [tickets, setTickets] = useState([]);
  useEffect(() => { ctx.api.get('/tickets').then(r => setTickets(r.data || [])).catch(() => setTickets([])); }, [ctx.api]);
  return (
    <div className="space-y-3" data-testid="live-tickets">
      <h4 className="text-xs font-heading text-zinc-400 uppercase">Support Tickets</h4>
      {tickets.length === 0 ? (
        <div className="bg-zinc-950 border border-zinc-800 rounded-md py-8 text-center">
          <p className="text-xs text-zinc-500">No support tickets raised by this vendor</p>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {tickets.slice(0, 10).map(t => (
            <div key={t.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-3 py-2">
              <div><p className="text-xs text-zinc-200">{t.subject || t.title}</p><p className="text-[10px] text-zinc-600">{t.created_at?.slice(0, 10)}</p></div>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${t.status === 'resolved' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>{t.status || 'open'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveProfileView({ ctx }) {
  const [data, setData] = useState(null);
  useEffect(() => { ctx.api.get('/vendor/my-dashboard').then(r => setData(r.data?.vendor)).catch(() => {}); }, [ctx.api]);
  if (!data) return <div className="text-xs text-zinc-600 animate-pulse py-4">Loading profile...</div>;
  return (
    <div className="space-y-3" data-testid="live-profile">
      <h4 className="text-xs font-heading text-zinc-400 uppercase">Vendor Profile</h4>
      <div className="bg-zinc-950 border border-zinc-800 rounded-md p-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
          <div><p className="text-[9px] text-zinc-600 uppercase">Company</p><p className="text-zinc-200">{data.company_name || '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">Entity</p><p className="text-zinc-200 capitalize">{data.entity_type || '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">Email</p><p className="text-zinc-200">{data.email || '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">Phone</p><p className="text-zinc-200">{data.phone || '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">NDA</p><p className={data.nda_signed ? 'text-emerald-400' : 'text-red-400'}>{data.nda_signed ? 'Signed' : 'Not Signed'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">Pricing</p><p className="text-zinc-200 capitalize">{(data.pricing_model || '').replace(/_/g, ' ')}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">GST</p><p className="text-zinc-200 font-mono">{data.gst_number || '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">PAN</p><p className="text-zinc-200 font-mono">{data.pan_number || '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">Bank</p><p className="text-zinc-200">{data.bank_name || '-'}</p></div>
          <div><p className="text-[9px] text-zinc-600 uppercase">IFSC</p><p className="text-zinc-200 font-mono">{data.bank_ifsc || '-'}</p></div>
        </div>
      </div>
    </div>
  );
}

function LiveMessagesView({ ctx }) {
  const [projects, setProjects] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [pid, setPid] = useState('');
  useEffect(() => { ctx.api.get('/projects').then(r => { setProjects(r.data || []); if (r.data?.length) setPid(r.data[0].id); }).catch(() => {}); }, [ctx.api]);
  useEffect(() => { if (pid) ctx.api.get(`/messages/thread?project_id=${pid}`).then(r => setMsgs(r.data?.messages || [])).catch(() => {}); }, [ctx.api, pid]);
  return (
    <div className="space-y-3" data-testid="live-messages">
      <h4 className="text-xs font-heading text-zinc-400 uppercase">Message Center</h4>
      <select value={pid} onChange={e => setPid(e.target.value)} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-300 px-2">
        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <div className="space-y-1 max-h-60 overflow-y-auto">
        {msgs.length === 0 ? <p className="text-xs text-zinc-600 text-center py-4">No messages</p> :
         msgs.slice(-20).map(m => (
          <div key={m.id} className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2">
            <div className="flex items-center gap-2"><span className="text-[10px] text-zinc-400 font-semibold">{m.sender_name}</span><span className="text-[9px] text-zinc-600">{m.created_at?.slice(0, 16)}</span></div>
            <p className="text-[11px] text-zinc-300 mt-0.5">{m.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveTeamView({ ctx }) {
  const [data, setData] = useState(null);
  useEffect(() => { ctx.api.get('/vendor/my-team').then(r => setData(r.data)).catch(() => {}); }, [ctx.api]);
  if (!data) return <div className="text-xs text-zinc-600 animate-pulse py-4">Loading team...</div>;
  const artists = data.invited_artists || [];
  return (
    <div className="space-y-3" data-testid="live-team">
      <h4 className="text-xs font-heading text-zinc-400 uppercase">My Team ({artists.length} artists)</h4>
      {artists.length === 0 ? <p className="text-xs text-zinc-600 text-center py-4">No artists registered</p> :
       artists.map(a => (
        <div key={a.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-3 py-2">
          <div><span className="text-xs text-zinc-200">{a.full_name}</span><span className="text-[10px] text-zinc-500 ml-2">{a.email}</span></div>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${a.is_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{a.is_active ? 'Active' : 'Inactive'}</span>
        </div>
      ))}
    </div>
  );
}

function LiveFinanceView({ ctx }) {
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const loadInvoices = () => ctx.api.get('/invoices/mine').then(r => setInvoices(r.data || [])).catch(() => setInvoices([]));
  useEffect(() => {
    ctx.api.get('/vendor/my-dashboard').then(r => setData(r.data)).catch(() => {});
    loadInvoices();
  }, [ctx.api]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="text-xs text-zinc-600 animate-pulse py-4">Loading finance...</div>;
  const wos = data.work_orders || [];
  const accepted = wos.filter(w => w.status === 'accepted');
  const total = accepted.reduce((s, w) => s + (w.total_amount || 0), 0);
  return (
    <div className="space-y-3" data-testid="live-finance">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-heading text-zinc-400 uppercase">Finance</h4>
        <button onClick={() => { setEditingInvoice(null); setShowBuilder(true); }}
          disabled={accepted.length === 0}
          data-testid="live-raise-invoice-btn"
          className="h-8 px-3 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-950 text-[11px] font-bold rounded-sm flex items-center gap-1.5 transition-colors">
          <Plus className="w-3 h-3" /> Raise Invoice
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-zinc-950 border border-zinc-800 rounded p-3 text-center"><p className="text-lg font-heading font-bold text-emerald-400">{'\u20B9'}{total.toLocaleString('en-IN')}</p><p className="text-[9px] text-zinc-500">Accepted WO Value</p></div>
        <div className="bg-zinc-950 border border-zinc-800 rounded p-3 text-center"><p className="text-lg font-heading font-bold text-amber-400">{wos.length}</p><p className="text-[9px] text-zinc-500">Total WOs</p></div>
        <div className="bg-zinc-950 border border-zinc-800 rounded p-3 text-center"><p className="text-lg font-heading font-bold text-white">{accepted.length}</p><p className="text-[9px] text-zinc-500">Accepted</p></div>
      </div>

      <InvoiceList
        invoices={invoices}
        onEdit={(inv) => { setEditingInvoice(inv); setShowBuilder(true); }}
        onDelete={async (inv) => {
          if (!window.confirm(`Delete draft invoice ${inv.invoice_no}?`)) return;
          try { await ctx.api.delete(`/invoices/${inv.id}`); await loadInvoices(); } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
        }}
      />

      <InvoiceBuilderModal
        open={showBuilder}
        onClose={() => { setShowBuilder(false); setEditingInvoice(null); }}
        workOrders={accepted}
        editingInvoice={editingInvoice}
        onCreated={() => { loadInvoices(); }}
      />
    </div>
  );
}

function LiveApprovalsView({ ctx }) {
  const [items, setItems] = useState([]);
  useEffect(() => { ctx.api.get('/reviews/inbox').then(r => setItems(r.data?.items || r.data || [])).catch(() => setItems([])); }, [ctx.api]);
  return (
    <div className="space-y-3" data-testid="live-approvals">
      <h4 className="text-xs font-heading text-zinc-400 uppercase">Approvals ({items.length})</h4>
      {items.length === 0 ? <p className="text-xs text-zinc-600 text-center py-4">No approvals pending</p> :
       items.slice(0, 15).map(item => (
        <div key={item.id} className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2">
          <span className="text-xs text-zinc-200">{item.title || item.name}</span>
          <span className={`ml-2 text-[9px] px-1.5 py-0.5 rounded-full ${
            item.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'
          }`}>{item.status || 'pending'}</span>
        </div>
      ))}
    </div>
  );
}

/* Lightweight vendor dashboard for live view — mirrors actual VendorDashboard tabs */
function VendorDashboardLive({ ctx }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState('dashboard');

  useEffect(() => {
    ctx.api.get('/vendor/my-dashboard').then(r => setData(r.data)).catch(console.error);
  }, [ctx.api]);

  if (!data) return <div className="animate-pulse text-zinc-600 text-sm py-4">Loading vendor dashboard...</div>;

  const tasks = data.tasks || {};
  const items = tasks.items || [];
  const myProjects = data.my_projects || [];
  const workOrders = data.work_orders || [];
  const pendingAcceptance = data.pending_acceptance || [];
  const hiddenCount = data.hidden_task_count || 0;
  const vendor = data.vendor || {};

  return (
    <div className="space-y-4" data-testid="live-vendor-dashboard">
      {/* Vendor name */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-heading font-bold text-white">{vendor.company_name || vendor.full_name || 'Vendor'} Dashboard</h3>
          <p className="text-[10px] text-zinc-500">Viewing as vendor &middot; {vendor.entity_type || ''}</p>
        </div>
      </div>

      {/* WO Acceptance banner */}
      {pendingAcceptance.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-2.5 flex items-center justify-between">
          <div>
            <p className="text-xs text-amber-400 font-heading font-bold">{pendingAcceptance.length} Work Order{pendingAcceptance.length > 1 ? 's' : ''} Pending Acceptance</p>
            {hiddenCount > 0 && <p className="text-[10px] text-zinc-500">{hiddenCount} tasks hidden until accepted</p>}
          </div>
          <button onClick={() => setView('workorders')} className="h-7 px-3 bg-amber-500 text-zinc-950 text-[10px] font-bold rounded">Review</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800">
        {[
          { k: 'dashboard', l: 'Dashboard' },
          { k: 'projects', l: `My Projects (${myProjects.length})` },
          { k: 'workorders', l: `Work Orders (${workOrders.length})` },
          { k: 'status', l: `Tasks (${tasks.total || 0})` },
        ].map(t => (
          <button key={t.k} onClick={() => setView(t.k)}
            className={`px-3 py-1.5 text-[10px] font-medium border-b-2 -mb-px ${view === t.k ? 'border-emerald-500 text-white' : 'border-transparent text-zinc-600 hover:text-zinc-300'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Dashboard tab */}
      {view === 'dashboard' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              { l: 'Projects', v: myProjects.length, c: 'text-emerald-400' },
              { l: 'Work Orders', v: workOrders.length, c: 'text-amber-400' },
              { l: 'Total Tasks', v: tasks.total || 0, c: 'text-blue-400' },
              { l: 'Completed', v: tasks.completed || 0, c: 'text-emerald-400' },
              { l: 'Total Value', v: `\u20B9${myProjects.reduce((s, p) => s + (p.commercial_value || 0), 0).toLocaleString('en-IN')}`, c: 'text-cyan-400', raw: true },
            ].map(s => (
              <div key={s.l} className="bg-zinc-950 border border-zinc-800 rounded p-3 text-center">
                <p className={`text-xl font-heading font-bold ${s.c}`}>{s.raw ? s.v : s.v}</p>
                <p className="text-[9px] text-zinc-500">{s.l}</p>
              </div>
            ))}
          </div>
          {myProjects.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Active Projects</p>
              {myProjects.slice(0, 5).map(p => (
                <div key={p.project_id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-3 py-2">
                  <div>
                    <span className="text-xs text-white font-semibold">{p.project_name}</span>
                    <span className="text-[10px] text-zinc-500 ml-2">{p.task_count} tasks</span>
                  </div>
                  <span className="text-[10px] text-emerald-400 font-mono">{'\u20B9'}{(p.commercial_value || 0).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          )}
          {myProjects.length === 0 && workOrders.length === 0 && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-md py-8 text-center">
              <p className="text-xs text-zinc-500">No projects or work orders assigned to this vendor yet</p>
              <p className="text-[10px] text-zinc-600 mt-1">Create a Work Order to assign work to this vendor</p>
            </div>
          )}
        </div>
      )}

      {/* Projects tab */}
      {view === 'projects' && (
        <div className="space-y-2">
          {myProjects.length === 0 ? (
            <p className="text-xs text-zinc-500 text-center py-6">No projects assigned</p>
          ) : myProjects.map(p => (
            <div key={p.project_id} className="bg-zinc-950 border border-zinc-800 rounded p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-white font-semibold">{p.project_name}</span>
                <span className="text-[10px] text-emerald-400 font-mono">{'\u20B9'}{(p.commercial_value || 0).toLocaleString('en-IN')}</span>
              </div>
              <p className="text-[10px] text-zinc-500 mt-0.5">{p.task_count} tasks &middot; {p.completed_tasks} completed &middot; {p.active_tasks} active</p>
            </div>
          ))}
        </div>
      )}

      {/* Work Orders tab */}
      {view === 'workorders' && (
        <div className="space-y-2">
          {workOrders.length === 0 ? (
            <p className="text-xs text-zinc-500 text-center py-6">No work orders</p>
          ) : workOrders.map(wo => (
            <div key={wo.id} className={`bg-zinc-950 border rounded px-3 py-2.5 ${wo.status === 'issued' ? 'border-amber-500/30' : 'border-zinc-800'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-white font-semibold">{wo.work_order_no}</span>
                  <span className="text-[10px] text-zinc-500 ml-2">{wo.project_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-amber-400">{'\u20B9'}{(wo.total_amount || 0).toLocaleString('en-IN')}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${wo.status === 'accepted' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
                    {(wo.status || 'draft').charAt(0).toUpperCase() + (wo.status || 'draft').slice(1)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tasks tab */}
      {view === 'status' && (
        items.length === 0 ? (
          <p className="text-xs text-zinc-500 text-center py-6">{hiddenCount > 0 ? `${hiddenCount} tasks hidden — vendor needs to accept Work Orders` : 'No tasks assigned'}</p>
        ) : (
          <LiveTasksWithRawMaterials ctx={ctx} tasks={items} />
        )
      )}
    </div>
  );
}

function LiveTasksWithRawMaterials({ ctx, tasks }) {
  // Reusable inline task list — filters raw materials per task_type using
  // the RAW_MATERIAL_DEPENDENCIES table (Sound vendors stop seeing Concept
  // Art, Animation vendors see Layout + Animatics, etc.).
  const [refs, setRefs] = useState({});
  const [expandedTask, setExpandedTask] = useState(null);

  useEffect(() => {
    ctx.api.get('/me/references').then(r => setRefs(r.data || {})).catch(() => {});
  }, [ctx.api]);

  if (!tasks || tasks.length === 0) return <p className="text-xs text-zinc-500 text-center py-6" data-testid="live-tasks-empty">No tasks assigned</p>;

  return (
    <div>
      <h4 className="text-xs font-heading text-zinc-400 uppercase mb-2">Tasks ({tasks.length})</h4>
      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
        {tasks.slice(0, 50).map(t => {
          const { counts, total, sections } = rawMaterialCounts(t, refs);
          const hasRaw = total > 0;
          const isExpanded = expandedTask === t.id;
          const pillBits = sections.map(s => {
            const n = counts[s.bucket] ?? s.items.length;
            return `${n} ${BUCKET_SHORT[s.bucket] || s.bucket}${n === 1 ? '' : 's'}`;
          });
          return (
            <div key={t.id} className="bg-zinc-950 border border-zinc-800 rounded-md" data-testid={`live-task-row-${t.id}`}>
              <div className="flex items-center justify-between px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{t.title || t.name || t.parent_code || (t.task_type ? t.task_type.replace(/_/g, ' ').toUpperCase() : '(unnamed task)')}</p>
                  <p className="text-[9px] text-zinc-500 mt-0.5">{t.task_type || ''} {t.parent_code ? `· ${t.parent_code}` : ''}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                  t.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                  t.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                  t.status === 'review' ? 'bg-purple-500/20 text-purple-400' :
                  'bg-zinc-700/50 text-zinc-400'
                }`}>{(t.status || 'assigned').replace(/_/g, ' ')}</span>
              </div>
              {hasRaw ? (
                <button onClick={() => setExpandedTask(isExpanded ? null : t.id)}
                  data-testid={`live-raw-toggle-${t.id}`}
                  className="w-full px-3 py-1.5 border-t border-zinc-800/50 flex items-center gap-1.5 text-[10px] text-cyan-400 hover:bg-zinc-900/50 transition-colors">
                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  Raw Materials ({pillBits.join(' · ')})
                </button>
              ) : (
                <p className="px-3 py-1.5 border-t border-zinc-800/50 text-[10px] text-zinc-600 italic">No raw materials uploaded by Production yet for this task type</p>
              )}
              {isExpanded && hasRaw && (
                <div className="px-3 py-3 border-t border-zinc-800/50 space-y-3 bg-zinc-900/30" data-testid={`live-raw-panel-${t.id}`}>
                  {sections.map(({ bucket, items }) => {
                    if (bucket === 'scripts') {
                      return (
                        <div key={bucket}>
                          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1.5">{BUCKET_LABEL[bucket]}</p>
                          <div className="space-y-1">
                            {items.map(s => {
                              const wc = (s.script || '').trim() ? s.script.trim().split(/\s+/).length : 0;
                              return (
                                <details key={s.project_id} className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
                                  <summary className="flex items-center gap-2 cursor-pointer">
                                    <FileText className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                                    <span className="text-[10px] text-zinc-300 truncate flex-1">{s.project_name}</span>
                                    <span className="text-[9px] text-zinc-500">{wc.toLocaleString()} words</span>
                                    <span className="text-[9px] text-cyan-400">read</span>
                                  </summary>
                                  {s.logline && <p className="text-[10px] text-zinc-300 italic mt-2 mb-1">"{s.logline}"</p>}
                                  {s.synopsis && <p className="text-[10px] text-zinc-300 mt-1 whitespace-pre-wrap">{s.synopsis}</p>}
                                  {s.script && <pre className="text-[10px] text-zinc-300 mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed">{s.script}</pre>}
                                </details>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    if (bucket === 'reference_links') {
                      return (
                        <div key={bucket}>
                          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1.5">{BUCKET_LABEL[bucket]}</p>
                          <div className="space-y-1">
                            {items.flatMap(p => (p.links || []).map((l, i) => (
                              <a key={`${p.project_id}-${i}`} href={l.url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 hover:border-orange-400/40 transition-colors"
                                data-testid={`live-link-${p.project_id}-${i}`}>
                                <LinkIcon className={`w-3 h-3 flex-shrink-0 ${l.type === 'youtube' ? 'text-rose-400' : l.type === 'mood_board' ? 'text-fuchsia-400' : 'text-orange-400'}`} />
                                <span className="text-[10px] text-zinc-300 truncate flex-1">{l.title || l.url}</span>
                                <span className="text-[8px] uppercase tracking-wider text-zinc-500">{l.type}</span>
                              </a>
                            )))}
                          </div>
                        </div>
                      );
                    }
                    if (bucket === 'vo') {
                      return (
                        <div key={bucket}>
                          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1.5">{BUCKET_LABEL[bucket]}</p>
                          <div className="space-y-1">
                            {items.slice(0, 8).map(v => (
                              <div key={v.id} className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5" data-testid={`live-vo-${v.id}`}>
                                <Music className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                                <span className="text-[10px] text-zinc-300 truncate flex-1">{v.title || 'Voice Over'}</span>
                                <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">Approved</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    // Submission grid (concept_art, animatics, asset_build, rigging, layout, animation, lighting, fx_comp)
                    return (
                      <div key={bucket}>
                        <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1.5">{BUCKET_LABEL[bucket]} ({items.length})</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {items.slice(0, 9).map(r => {
                            const firstMedia = (r.media || [])[0] || {};
                            const thumbSrc = firstMedia.url || (firstMedia.storage_key ? `/api/reviews/media?key=${encodeURIComponent(firstMedia.storage_key)}` : '');
                            return (
                              <div key={r.id} className="relative bg-zinc-950 border border-zinc-800 rounded overflow-hidden" data-testid={`live-${bucket}-${r.id}`}>
                                {thumbSrc ? (
                                  <AuthImage src={thumbSrc} alt={r.title || ''} className="w-full h-16 object-cover" />
                                ) : (
                                  <div className="w-full h-16 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-zinc-700" /></div>
                                )}
                                <div className="px-1.5 py-1">
                                  <p className="text-[9px] text-zinc-300 truncate">{r.title || 'Untitled'}</p>
                                  <span className={`text-[8px] px-1 py-0.5 rounded-full ${
                                    r.approval_state === 'approved' ? 'bg-emerald-500/15 text-emerald-400' :
                                    r.approval_state === 'changes_requested' ? 'bg-amber-500/15 text-amber-400' :
                                    'bg-blue-500/15 text-blue-400'
                                  }`}>{(r.approval_state || 'pending').replace(/_/g, ' ')}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FreelancerDashboardLive({ ctx }) {
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    ctx.api.get('/tasks').then(r => setTasks(r.data || [])).catch(() => {});
  }, [ctx.api]);

  const byStatus = {};
  tasks.forEach(t => { byStatus[t.status] = (byStatus[t.status] || 0) + 1; });

  return (
    <div className="space-y-4" data-testid="live-freelancer-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { l: 'Total', v: tasks.length, c: 'text-white' },
          { l: 'In Progress', v: byStatus.in_progress || 0, c: 'text-blue-400' },
          { l: 'Completed', v: byStatus.completed || 0, c: 'text-emerald-400' },
          { l: 'Review', v: byStatus.review || 0, c: 'text-purple-400' },
          { l: 'Blocked', v: byStatus.blocked || 0, c: 'text-red-400' },
        ].map(s => (
          <div key={s.l} className="bg-zinc-950 border border-zinc-800 rounded p-3 text-center">
            <p className={`text-xl font-heading font-bold ${s.c}`}>{s.v}</p>
            <p className="text-[9px] text-zinc-500">{s.l}</p>
          </div>
        ))}
      </div>
      <LiveTasksWithRawMaterials ctx={ctx} tasks={tasks} />
    </div>
  );
}

function EmployeeDashboardLive({ ctx }) {
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    ctx.api.get('/tasks').then(r => setTasks(r.data || [])).catch(() => {});
  }, [ctx.api]);

  const byStatus = {};
  tasks.forEach(t => { byStatus[t.status] = (byStatus[t.status] || 0) + 1; });

  return (
    <div className="space-y-4" data-testid="live-employee-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { l: 'Total', v: tasks.length, c: 'text-white' },
          { l: 'In Progress', v: byStatus.in_progress || 0, c: 'text-blue-400' },
          { l: 'Completed', v: byStatus.completed || 0, c: 'text-emerald-400' },
          { l: 'Review', v: byStatus.review || 0, c: 'text-purple-400' },
          { l: 'Not Started', v: byStatus.not_started || 0, c: 'text-zinc-400' },
        ].map(s => (
          <div key={s.l} className="bg-zinc-950 border border-zinc-800 rounded p-3 text-center">
            <p className={`text-xl font-heading font-bold ${s.c}`}>{s.v}</p>
            <p className="text-[9px] text-zinc-500">{s.l}</p>
          </div>
        ))}
      </div>
      <LiveTasksWithRawMaterials ctx={ctx} tasks={tasks} />
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   WO DETAIL VIEWER MODAL
   ═══════════════════════════════════════════════════════════ */
function WODetailViewer({ wo, onClose }) {
  const { api } = useAuth();
  const [pdfBusy, setPdfBusy] = useState(false);
  if (!wo) return null;
  const lines = wo.rate_card || wo.lines || [];
  const milestones = wo.payment_terms?.milestones || [];
  const legal = wo.legal_terms || {};
  const onDownload = async () => {
    setPdfBusy(true);
    try { await downloadWorkOrderPdf(api, wo); } catch (e) { /* swallow — user can retry */ }
    finally { setPdfBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="wo-detail-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-md w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400">{wo.work_order_no}</p>
            <h2 className="text-xl font-heading font-bold text-white">{wo.scope_title || 'Work Order'}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onDownload}
              disabled={pdfBusy}
              data-testid="wo-detail-download-pdf"
              className="h-8 px-3 rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-wider flex items-center gap-1.5 hover:bg-amber-500/20 disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" /> {pdfBusy ? 'Preparing…' : 'PDF'}
            </button>
            <button onClick={onClose} data-testid="wo-detail-close" className="w-8 h-8 rounded border border-zinc-800 hover:bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-5 text-xs">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><span className="text-zinc-500 block mb-0.5">Status</span><span className="text-white font-semibold capitalize">{wo.status || 'draft'}</span></div>
            <div><span className="text-zinc-500 block mb-0.5">Project</span><span className="text-white">{wo.project_name || '-'}</span></div>
            <div><span className="text-zinc-500 block mb-0.5">Total Amount</span><span className="text-amber-400 font-mono font-bold">{currency(wo.total_amount)}</span></div>
            <div><span className="text-zinc-500 block mb-0.5">Created</span><span className="text-zinc-300 font-mono">{formatDate(wo.created_at)}</span></div>
          </div>
          {lines.length > 0 && (
            <div>
              <h4 className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Rate Card ({lines.length} items)</h4>
              <div className="space-y-1">
                {lines.map((l, i) => (
                  <div key={i} className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded px-3 py-2">
                    <div>
                      <span className="text-white">{l.task || l.section}</span>
                      <span className="text-zinc-600 ml-2">{l.stage}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-zinc-400">{l.quantity} x {currency(l.rate)}</span>
                      <span className="text-emerald-400 ml-3 font-mono">{currency(Number(l.quantity || 0) * Number(l.rate || 0))}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {milestones.length > 0 && (
            <div>
              <h4 className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Payment Milestones</h4>
              <div className="grid grid-cols-3 gap-2">
                {milestones.map((m, i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded p-3">
                    <p className="text-white font-semibold">{m.name}</p>
                    <p className="text-zinc-500 text-[10px]">{m.trigger}</p>
                    <p className="text-amber-400 font-mono mt-1">{m.percent}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(legal.ip_ownership || legal.confidentiality) && (
            <div>
              <h4 className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Legal Terms</h4>
              <div className="space-y-1 text-[11px]">
                {legal.ip_ownership && <p><span className="text-zinc-500">IP:</span> <span className="text-zinc-300">{legal.ip_ownership}</span></p>}
                {legal.usage_rights && <p><span className="text-zinc-500">Usage:</span> <span className="text-zinc-300">{legal.usage_rights}</span></p>}
                {legal.confidentiality && <p><span className="text-zinc-500">NDA:</span> <span className="text-zinc-300">{legal.confidentiality}</span></p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function VendorCommandCenter({ vendorId }) {
  const { api, user, token } = useAuth();
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(vendorId || '');
  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [woMsg, setWoMsg] = useState('');
  const [showWorkOrderBuilder, setShowWorkOrderBuilder] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // 'wo' | 'alerts' | null
  const [viewingWO, setViewingWO] = useState(null);
  const [editingWO, setEditingWO] = useState(null);
  const [liveViewActive, setLiveViewActive] = useState(false);
  // WO-pending badge map (user_id → {count, amount}). Founder ask
  // (May 14, 2026): "It's hard to remember what shots I assigned to
  // each vendor." This per-vendor badge surfaces unbilled allotments
  // at a glance.
  const [pendingByUser, setPendingByUser] = useState({});

  useEffect(() => {
    api.get('/vendor/list?include_freelancers=true').then(r => { setVendors(r.data); if (!selectedVendor && r.data.length > 0) setSelectedVendor(r.data[0].id); }).catch(() => {});
    api.get('/projects').then(r => { setProjects(r.data || []); }).catch(() => {});
    // Studio-wide pending summary — no project filter so PMs see all
    // outstanding allotments across every project per vendor.
    api.get('/work-orders/wo-pending-summary').then(r => {
      const map = {};
      (r.data?.rows || []).forEach(row => {
        if (row.user_id) map[row.user_id] = {
          count: row.unbilled_task_count,
          amount: row.unbilled_amount,
        };
        if (row.vendor_id) map[row.vendor_id] = {
          count: row.unbilled_task_count,
          amount: row.unbilled_amount,
        };
      });
      setPendingByUser(map);
    }).catch(() => setPendingByUser({}));
  }, [api]);

  useEffect(() => {
    if (!selectedVendor) return;
    // Founder rule (May 12, 2026): freelancers in this dropdown use the
    // SAME id as their user_id (they don't have a vendors row). The
    // /command-center endpoint falls back gracefully, but /work-orders
    // must be queried via assignee_user_id for freelancers — otherwise
    // their WOs (which lack vendor_id) won't show up.
    const sv = vendors.find(v => v.id === selectedVendor);
    const isFreelancer = sv && (sv.kind === 'freelancer' || sv.entity_type === 'freelancer');
    api.get(`/vendor/${selectedVendor}/command-center`).then(r => setData(r.data)).catch(console.error);
    const woUrl = isFreelancer
      ? `/work-orders?assignee_user_id=${selectedVendor}`
      : `/work-orders?vendor_id=${selectedVendor}`;
    api.get(woUrl).then(r => setWorkOrders(r.data || [])).catch(() => setWorkOrders([]));
  }, [api, selectedVendor, vendors]);

  const handleWorkOrderCreated = (workOrder) => {
    if (editingWO) {
      setWorkOrders(prev => prev.map(wo => wo.id === editingWO.id ? workOrder : wo));
    } else {
      setWorkOrders([workOrder, ...workOrders]);
    }
    setWoMsg(`${workOrder.work_order_no} ${editingWO ? 'updated' : 'generated'}`);
    setShowWorkOrderBuilder(false);
    setEditingWO(null);
    setTimeout(() => setWoMsg(''), 3500);
  };

  const deleteWO = async (woId) => {
    if (!window.confirm('Are you sure you want to delete this Work Order? This cannot be undone.')) return;
    try {
      await api.delete(`/work-orders/${woId}`);
      setWorkOrders(prev => prev.filter(wo => wo.id !== woId));
      setWoMsg('Work Order deleted');
      setTimeout(() => setWoMsg(''), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (!data) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-pulse text-zinc-600 text-sm font-heading">Loading Command Center...</div>
    </div>
  );

  const selectedVendorObj = vendors.find(v => v.id === selectedVendor);

  return (
    <div className="w-full max-w-[1600px] mx-auto space-y-6" data-testid="vendor-command-center">
      {/* ═══ WORK ORDER BUILDER MODAL ═══ */}
      {showWorkOrderBuilder && (
        <WorkOrderBuilder
          api={api}
          user={user}
          selectedVendor={selectedVendor}
          selectedKind={selectedVendorObj?.kind || selectedVendorObj?.entity_type}
          projects={projects}
          editingWO={editingWO}
          onClose={() => { setShowWorkOrderBuilder(false); setEditingWO(null); }}
          onCreated={handleWorkOrderCreated}
        />
      )}

      {/* ═══ WO DETAIL VIEWER ═══ */}
      {viewingWO && <WODetailViewer wo={viewingWO} onClose={() => setViewingWO(null)} />}

      {/* ═══ HEADER ═══ */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight" data-testid="page-title">
            Vendor & Freelancer Command Center
          </h1>
          {selectedVendorObj && (
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${
                (selectedVendorObj.kind === 'freelancer' || selectedVendorObj.entity_type === 'freelancer')
                  ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                  : 'bg-blue-500/15 text-blue-300 border-blue-500/40'
              }`} data-testid="vendor-kind-pill">
                <span className={`w-1.5 h-1.5 rounded-full ${
                  (selectedVendorObj.kind === 'freelancer' || selectedVendorObj.entity_type === 'freelancer')
                    ? 'bg-amber-400' : 'bg-blue-400'
                }`} />
                {(selectedVendorObj.kind === 'freelancer' || selectedVendorObj.entity_type === 'freelancer')
                  ? 'Freelancer' : 'Vendor'}
              </span>
              <p className="text-xs text-zinc-500">
                {selectedVendorObj.company_name || selectedVendorObj.full_name}
                {selectedVendorObj.entity_type && selectedVendorObj.entity_type !== 'freelancer'
                  ? ` · ${selectedVendorObj.entity_type}` : ''}
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {woMsg && <span className="text-xs text-emerald-400 font-mono animate-pulse" data-testid="wo-success-msg">{woMsg}</span>}
          <VendorFreelancerPicker
            vendors={vendors}
            selectedId={selectedVendor}
            onChange={(id) => setSelectedVendor(id)}
            pendingByUser={pendingByUser}
          />
        </div>
      </div>

      {/* ═══ 1. TOP ACTION BAR ═══ */}
      <ActionBar
        alertCount={data.alerts?.length || 0}
        onCreateWO={() => setShowWorkOrderBuilder(true)}
        onViewWO={() => { setActivePanel(activePanel === 'wo' ? null : 'wo'); setLiveViewActive(false); }}
        onToggleAlerts={() => { setActivePanel(activePanel === 'alerts' ? null : 'alerts'); setLiveViewActive(false); }}
        onLiveView={() => setLiveViewActive(!liveViewActive)}
        showAlerts={activePanel}
        liveViewActive={liveViewActive}
      />

      {/* ═══ LIVE VIEW MODE ═══ */}
      {liveViewActive ? (
        <ImpersonatedDashboard
          vendorId={selectedVendor}
          vendors={vendors}
          adminToken={token}
          onClose={() => setLiveViewActive(false)}
        />
      ) : (<>

      {/* ═══ ALERTS PANEL (expanded) ═══ */}
      {activePanel === 'alerts' && (
        <div className="space-y-1.5" data-testid="alerts-section">
          {data.alerts?.length > 0 ? data.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-xs ${
              a.severity === 'high' ? 'bg-red-500/10 border border-red-500/20 text-red-400' :
              a.severity === 'medium' ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400' :
              'bg-zinc-800/50 border border-zinc-700 text-zinc-400'
            }`}>
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{a.message}</span>
            </div>
          )) : (
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 text-xs text-zinc-500 text-center">
              No active alerts
            </div>
          )}
        </div>
      )}

      {/* ═══ ACTIVE WORK ORDERS LIST (expanded) ═══ */}
      {activePanel === 'wo' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="active-wo-list">
          <h3 className="text-sm font-heading font-bold text-white uppercase mb-3">All Work Orders ({workOrders.length})</h3>
          {workOrders.length === 0 ? (
            <p className="text-xs text-zinc-500 text-center py-4">No work orders</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {workOrders.map(wo => (
                <div key={wo.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-4 py-3 hover:border-zinc-600 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-amber-500" />
                    <div>
                      <p className="text-xs text-white font-semibold">{wo.work_order_no}</p>
                      <p className="text-[10px] text-zinc-500">{wo.scope_title || wo.project_name || '-'} &middot; {currency(wo.total_amount)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                      wo.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' :
                      wo.status === 'issued' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>{(wo.status || 'draft').charAt(0).toUpperCase() + (wo.status || 'draft').slice(1)}</span>
                    <button onClick={() => { setViewingWO(wo); setActivePanel(null); }} className="h-7 px-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[10px] rounded hover:bg-amber-500/20">Open</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ DEFAULT ALERTS (always show if no panel active) ═══ */}
      {!activePanel && data.alerts?.length > 0 && (
        <div className="space-y-1.5" data-testid="alerts-section-default">
          {data.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-xs ${
              a.severity === 'high' ? 'bg-red-500/10 border border-red-500/20 text-red-400' :
              a.severity === 'medium' ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400' :
              'bg-zinc-800/50 border border-zinc-700 text-zinc-400'
            }`}>
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* ═══ 2. WORK ORDERS HERO ═══ */}
      <WorkOrdersHero
        workOrders={workOrders}
        onCreateWO={() => setShowWorkOrderBuilder(true)}
        onOpenWO={(wo) => setViewingWO(wo)}
        onEditWO={(wo) => { setEditingWO(wo); setShowWorkOrderBuilder(true); }}
        onDeleteWO={deleteWO}
      />

      {/* ═══ 3. ALLOTTED WORK STATUS + DEADLINES & RISKS ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <AllottedWorkStatus departments={data.departments} />
        </div>
        <div className="lg:col-span-2">
          <DeadlinesRisks deadlines={data.deadlines} overdueTasks={data.overdue_tasks} />
        </div>
      </div>

      {/* ═══ 4. TASK BOARD (KANBAN) ═══ */}
      <TaskBoard kanban={data.kanban} />

      {/* ═══ 5. PERFORMANCE OVERVIEW (BOTTOM) ═══ */}
      <PerformanceOverview data={data} />

      </>)}
    </div>
  );
}
